import type { Advisor } from "../types"

import Dot from "@/assets/svg/advisors/Dot.svg?react"
import MessageTick from "@/assets/svg/lpr4/advisor/message-tick.svg?react"
import Chevron from "@/assets/svg/lpr4/advisor/chevron-right.svg?react"
import MoonEclipse from "@/assets/svg/lpr4/advisor/moon-eclipse.svg?react"
import MoonStar from "@/assets/svg/lpr4/advisor/moon-star.svg?react"
import MoonHalf from "@/assets/svg/lpr4/advisor/moon-half.svg?react"

const presetIcons = [<MoonEclipse />, <MoonStar />, <MoonHalf />]

interface AdvisorAdsCardProps {
  advisor: Advisor
  message: string
  /** Conversation starters shown as tappable options under the message. */
  presets: string[]
  onPresetClick: (preset: string) => void
}

/** Advisor card: avatar, name, experience, online status, message + conversation starters. Ported from AdvisorAdsCard. */
export function AdvisorAdsCard({ advisor, message, presets, onPresetClick }: AdvisorAdsCardProps) {
  return (
    <div className="relative w-full select-none rounded-3xl bg-purple-light/10 p-4 backdrop-blur-[30px]">
      <div className="flex items-center">
        <div className="relative inline-flex size-14 rounded-full bg-gradient-to-br from-peach via-peach to-purple-accent p-1">
          <span className="absolute inset-px rounded-full bg-dark-base" />
          <img
            src={advisor.photo}
            alt={advisor.name}
            className="relative size-full rounded-full object-cover object-top"
          />
        </div>
        <div className="ml-2 flex flex-col">
          <p className="font-display text-sm font-medium text-white">{advisor.name}</p>
          <p className="font-body text-xs text-purple-light/70">{advisor.exp}</p>
        </div>
        <div className="ml-auto flex h-7 items-center justify-center gap-1 text-nowrap rounded-2xl bg-green/20 pl-2.5 pr-3 font-body text-xs text-green [&_svg]:size-1.5">
          <Dot /> Online
        </div>
      </div>

      <div className="relative mt-3 max-w-[296px] rounded-2xl bg-[#362d45] px-4 py-3 font-body text-base text-purple-light [&_svg]:absolute [&_svg]:-bottom-0.5 [&_svg]:-left-0.5 [&_svg]:min-h-3 [&_svg]:min-w-3.5 [&_svg]:text-[#362d45]">
        {message}
        <MessageTick />
      </div>

      <div className="ml-auto mt-4 flex w-[90%] flex-col gap-2">
        {presets.map((preset, index) => (
          <button
            type="button"
            key={preset}
            onClick={() => onPresetClick(preset)}
            className="relative flex select-none items-center gap-2 rounded-xl border border-purple-light/10 px-[18px] py-3.5 text-left font-body text-base text-purple-light [&_svg]:size-6"
          >
            <span className="size-6">{presetIcons[index % presetIcons.length]}</span>
            {preset}
            <span className="absolute right-2 top-1/2 flex -translate-y-1/2 items-center justify-center text-purple-light">
              <Chevron />
            </span>
          </button>
        ))}
      </div>
    </div>
  )
}
